/**
 * ShiftTasksPage - Manage shift tasks and crew assignments
 * PROMPT 34: Task-Level Crew Assignment (Foundations)
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getShift,
  getShiftTasks,
  getProject,
  getVenue,
  getVenueLocation,
  getTaskDefinitions,
  extractErrorMessage,
} from "../lib/api";
import {
  LoadingSpinner,
  EmptyState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { TaskCrewAssignment } from "../components/tasks/TaskCrewAssignment";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Separator } from "../components/ui/separator";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  FolderKanban,
  ClipboardList,
  Users,
} from "lucide-react";

export default function ShiftTasksPage() {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const { formatDate, formatTime, formatCurrency } = useFormatters();
  
  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState(null);
  const [project, setProject] = useState(null);
  const [location, setLocation] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskDefinitions, setTaskDefinitions] = useState({});
  
  // Load shift and tasks
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load shift
      const shiftRes = await getShift(shiftId);
      const shiftData = shiftRes.data;
      setShift(shiftData);
      
      // Load project, venue and venue location
      const [projectRes, venueRes, venueLocationRes] = await Promise.all([
        shiftData.project_id ? getProject(shiftData.project_id) : null,
        shiftData.venue_id ? getVenue(shiftData.venue_id) : null,
        shiftData.venue_location_id ? getVenueLocation(shiftData.venue_location_id) : null,
      ]);
      setProject(projectRes?.data || null);
      // Use venue data for location display
      setLocation(venueRes?.data ? { 
        ...venueRes.data, 
        venue_location: venueLocationRes?.data?.name 
      } : null);
      
      // Load tasks for this shift
      const tasksRes = await getShiftTasks({ shift_id: shiftId });
      setTasks(tasksRes.data || []);
      
      // Load task definitions for names
      // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1: Include disabled
      // task definitions so historical line items still render their snapshot names.
      const taskDefsRes = await getTaskDefinitions({ limit: 100, enabled_filter: "all" });
      const taskDefsMap = {};
      (taskDefsRes.data || []).forEach((td) => {
        taskDefsMap[td.id] = td;
      });
      setTaskDefinitions(taskDefsMap);
      
    } catch (err) {
      console.error("Failed to load data:", extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [shiftId]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!shift) {
    return (
      <EmptyState
        title="Shift not found"
        description="The shift you're looking for doesn't exist or has been deleted."
        action={
          <Button onClick={() => navigate("/shifts")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Shifts
          </Button>
        }
      />
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Shift Tasks & Crew</h1>
            <p className="text-muted-foreground">
              {shift.name || `Shift on ${formatDate(shift.date)}`}
            </p>
          </div>
        </div>
      </div>
      
      {/* Shift Info Card */}
      <Card data-testid="shift-info-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Shift Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Date</p>
              <p className="font-medium">{formatDate(shift.date)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Time</p>
              <p className="font-medium flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Project</p>
              <p className="font-medium flex items-center gap-1">
                <FolderKanban className="h-4 w-4" />
                {project?.name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Location</p>
              <p className="font-medium flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {location?.name || "N/A"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Tasks Section */}
      <Card data-testid="tasks-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Tasks
            {tasks.length > 0 && (
              <Badge variant="secondary">{tasks.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Manage crew assignments for each task in this shift
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="py-8 text-center border border-dashed border-border/50 rounded-md">
              <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No tasks defined for this shift</p>
              <p className="text-sm text-muted-foreground mt-1">
                Tasks are created when setting up the shift in the project
              </p>
            </div>
          ) : (
            <Accordion type="multiple" defaultValue={tasks.map((t) => t.id)} className="space-y-2">
              {tasks.map((task) => {
                const taskDef = taskDefinitions[task.task_definition_id];
                return (
                  <AccordionItem
                    key={task.id}
                    value={task.id}
                    className="border rounded-lg px-4"
                    data-testid={`task-item-${task.id}`}
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium flex items-center gap-2">
                            {taskDef?.name || "Unknown Task"}
                            {/* Task Type Badge */}
                            {taskDef?.task_type === "role" && (
                              <Badge variant="secondary" className="text-[10px] bg-blue-500/20 text-blue-400">
                                Labour
                              </Badge>
                            )}
                            {(taskDef?.task_type === "supplement" || taskDef?.task_type === "allowance") && (
                              <Badge variant="secondary" className="text-[10px] bg-amber-500/20 text-amber-400">
                                Supplement
                              </Badge>
                            )}
                            {taskDef?.task_type === "item" && (
                              <Badge variant="secondary" className="text-[10px] bg-purple-500/20 text-purple-400">
                                Item
                              </Badge>
                            )}
                            {/* Rule-added indicator */}
                            {task.rule_added && (
                              <Badge variant="outline" className="text-[10px] border-teal-500/50 text-teal-400">
                                Rule
                              </Badge>
                            )}
                          </p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            {/* Supplement Group for supplements */}
                            {taskDef?.supplement_group && (
                              <span className="capitalize text-xs">
                                {taskDef.supplement_group.replace(/_/g, " ")}
                              </span>
                            )}
                            <span>
                              Rate: {formatCurrency(task.charge_rate || taskDef?.default_charge_rate || 0)}
                              /{task.charge_unit || taskDef?.default_charge_unit || "hourly"}
                            </span>
                            {taskDef?.required_skills?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Badge variant="outline" className="text-xs">
                                  {taskDef.required_skills.length} skill(s) required
                                </Badge>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <Separator className="mb-4" />
                      <TaskCrewAssignment shiftTaskId={task.id} />
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
